import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API, useAuth, getAuthenticatedMediaUrl } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Image,
  FileText,
  Download,
  ExternalLink,
  RefreshCw,
  Play,
  Loader2,
  X,
  Search,
  Filter,
  Calendar,
  Video,
  Music,
  ImageIcon,
  FileIcon,
  User,
  AlertTriangle,
  ChevronDown,
  Check,
  Link2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Searchable Select Component
function SearchableSelect({ value, onValueChange, options, placeholder, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(opt =>
      opt.label.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between h-9 bg-slate-900 border-slate-700 hover:bg-slate-800 ${className}`}
        >
          <span className="truncate text-left flex-1">
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-slate-900 border-slate-700" align="start">
        <div className="p-2 border-b border-slate-700">
          <Input
            ref={inputRef}
            placeholder="Type to search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 bg-slate-800 border-slate-600"
          />
        </div>
        <ScrollArea className="h-[200px]">
          <div className="p-1">
            {filteredOptions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No results found</p>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-800 text-left ${
                    value === option.value ? "bg-slate-800 text-white" : "text-slate-300"
                  }`}
                >
                  <Check className={`h-4 w-4 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default function Media() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterMediaType, setFilterMediaType] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterSourceType, setFilterSourceType] = useState(""); // "incident", "perpetrator", or ""
  const [filterIncidentId, setFilterIncidentId] = useState("");
  const [filterProfileId, setFilterProfileId] = useState("");
  const [filterLinkedPerpId, setFilterLinkedPerpId] = useState(""); // Perpetrators linked to incidents

  // Incident types for filter
  const [incidentTypes, setIncidentTypes] = useState([]);
  // Incidents list for specific incident filter
  const [incidents, setIncidents] = useState([]);
  // Perpetrator profiles for filter
  const [perpetrators, setPerpetrators] = useState([]);
  // Linked perpetrators (from incidents' perp_descriptions)
  const [linkedPerpetrators, setLinkedPerpetrators] = useState([]);

  useEffect(() => {
    fetchMedia();
    fetchIncidentTypes();
    fetchIncidents();
    fetchPerpetrators();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/uploads/media-library`, { withCredentials: true });
      setMedia(response.data || []);
    } catch (error) {
      console.error("Failed to fetch media:", error);
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidentTypes = async () => {
    try {
      const response = await axios.get(`${API}/incident-types`, { withCredentials: true });
      setIncidentTypes(response.data || []);
    } catch (error) {
      console.error("Failed to fetch incident types:", error);
    }
  };

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents?limit=500`, { withCredentials: true });
      const incidentsList = response.data?.incidents || [];
      setIncidents(incidentsList);
    } catch (error) {
      console.error("Failed to fetch incidents:", error);
    }
  };

  // Build linked perpetrators list from media data and incidents
  useEffect(() => {
    const linkedPerpsMap = new Map();

    // Add perpetrators who have profile media (mugshots, IDs, etc.)
    media.forEach(item => {
      if (item.profile_id && item.profile_name) {
        linkedPerpsMap.set(item.profile_id, {
          profile_id: item.profile_id,
          name: item.profile_name
        });
      }
    });

    // Add perpetrators linked to incidents that have media
    const incidentIdsWithMedia = new Set(media.filter(m => m.incident_id).map(m => m.incident_id));
    incidents.forEach(inc => {
      if (incidentIdsWithMedia.has(inc.incident_id)) {
        (inc.perp_descriptions || []).forEach(perp => {
          if (perp.linked_profile_id && perp.name) {
            linkedPerpsMap.set(perp.linked_profile_id, {
              profile_id: perp.linked_profile_id,
              name: perp.name
            });
          }
        });
      }
    });

    setLinkedPerpetrators(Array.from(linkedPerpsMap.values()));
  }, [media, incidents]);

  const fetchPerpetrators = async () => {
    try {
      const response = await axios.get(`${API}/perpetrator-profiles`, { withCredentials: true });
      setPerpetrators(response.data?.profiles || response.data || []);
    } catch (error) {
      console.error("Failed to fetch perpetrators:", error);
    }
  };

  const getMediaUrl = (item) => {
    // Build URL from file_id or url, then authenticate.
    // IMPORTANT: must be absolute (start with API host). Frontend and backend
    // sit on different Railway domains, so a "/api/uploads/..." path resolves
    // against the frontend origin and 404s. getAuthenticatedMediaUrl preserves
    // whatever URL it gets when it already matches the uploads pattern, so
    // we have to feed it the absolute one.
    let url = item.url;
    if (!url && item.file_id) {
      url = `${API}/uploads/${item.file_id}`;
    }
    if (!url && item.filename) {
      url = `${API}/uploads/${item.filename}`;
    }
    return getAuthenticatedMediaUrl(url);
  };

  const getThumbnailUrl = (item) => {
    // Use fast thumbnail endpoint for grid — no watermarking, much lighter on backend
    const fileId = item.file_id || (item.url ? item.url.split('/').pop().split('?')[0] : item.filename);
    if (!fileId) return getMediaUrl(item);
    const sessionToken = localStorage.getItem('session_token');
    const base = `${API}/uploads/${fileId}/thumb?size=300`;
    return sessionToken ? `${base}&token=${sessionToken}` : base;
  };

  const isImage = (item) => {
    const contentType = item.content_type || "";
    const filename = item.filename || item.url || "";
    return contentType.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)($|\?)/i.test(filename);
  };

  const isVideo = (item) => {
    const contentType = item.content_type || "";
    const filename = item.filename || item.url || "";
    return contentType.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv)($|\?)/i.test(filename);
  };

  const isAudio = (item) => {
    const contentType = item.content_type || "";
    const filename = item.filename || item.url || "";
    return contentType.startsWith("audio/") || /\.(mp3|wav|m4a|ogg|aac|flac)($|\?)/i.test(filename);
  };

  // Get media type string
  const getMediaType = (item) => {
    if (isImage(item)) return "image";
    if (isVideo(item)) return "video";
    if (isAudio(item)) return "audio";
    return "other";
  };

  // Filter media based on all filters
  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      // NEVER show audio files in media library
      if (isAudio(item)) return false;

      // Search query - search in headline, address, type, perpetrator name
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesHeadline = item.incident_headline?.toLowerCase().includes(query);
        const matchesAddress = item.incident_address?.toLowerCase().includes(query);
        const matchesType = item.incident_type?.toLowerCase().includes(query);
        const matchesProfile = item.profile_name?.toLowerCase().includes(query);
        // Standalone library items have no incident or profile text, so without
        // this they could never match a search.
        const matchesFilename = item.filename?.toLowerCase().includes(query);
        if (!matchesHeadline && !matchesAddress && !matchesType && !matchesProfile
            && !matchesFilename) {
          return false;
        }
      }

      // Source type filter (incident vs perpetrator)
      if (filterSourceType && filterSourceType !== "all") {
        if (item.source_type !== filterSourceType) return false;
      }

      // Specific incident filter
      if (filterIncidentId && filterIncidentId !== "all") {
        if (item.incident_id !== filterIncidentId) return false;
      }

      // Specific perpetrator filter (perpetrator profile media)
      if (filterProfileId && filterProfileId !== "all") {
        if (item.profile_id !== filterProfileId) return false;
      }

      // Linked perpetrator filter - ONLY shows incident media where this perp was linked
      // Does NOT include the perp's own profile media (that's what "Perp Profile Media" filter is for)
      if (filterLinkedPerpId && filterLinkedPerpId !== "all") {
        // Exclude profile media - only show incident media
        if (item.source_type === "perpetrator") return false;

        // Check if incident has this perpetrator linked
        const incident = incidents.find(inc => inc.incident_id === item.incident_id);
        if (!incident) return false;
        const hasLinkedPerp = (incident.perp_descriptions || []).some(
          perp => perp.linked_profile_id === filterLinkedPerpId
        );
        if (!hasLinkedPerp) return false;
      }

      // Incident type filter
      if (filterType && filterType !== "all") {
        if (item.incident_type !== filterType) return false;
      }

      // Media type filter
      if (filterMediaType && filterMediaType !== "all") {
        if (getMediaType(item) !== filterMediaType) return false;
      }

      // Date range filter
      if (filterStartDate || filterEndDate) {
        const itemDate = item.incident_date || item.uploaded_at ? new Date(item.incident_date || item.uploaded_at) : null;
        if (itemDate) {
          if (filterStartDate) {
            const startDate = new Date(filterStartDate);
            startDate.setHours(0, 0, 0, 0);
            if (itemDate < startDate) return false;
          }
          if (filterEndDate) {
            const endDate = new Date(filterEndDate);
            endDate.setHours(23, 59, 59, 999);
            if (itemDate > endDate) return false;
          }
        }
      }

      return true;
    });
  }, [media, incidents, searchQuery, filterType, filterMediaType, filterStartDate, filterEndDate, filterSourceType, filterIncidentId, filterProfileId, filterLinkedPerpId]);

  // Check if any filters are active
  const hasActiveFilters = searchQuery || filterType || filterMediaType || filterStartDate || filterEndDate || filterSourceType || filterIncidentId || filterProfileId || filterLinkedPerpId;

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterMediaType("");
    setFilterStartDate("");
    setFilterEndDate("");
    setFilterSourceType("");
    setFilterIncidentId("");
    setFilterProfileId("");
    setFilterLinkedPerpId("");
  };

  // Navigate to source incident
  const goToSource = (item) => {
    if (!item || !item.incident_id) return;
    setSelectedMedia(null);
    navigate(`/incidents/${item.incident_id}`);
  };

  // Navigate to perpetrator profile
  const goToProfile = (item) => {
    if (!item || !item.profile_id) return;
    setSelectedMedia(null);
    navigate(`/profiles-admin?profile=${item.profile_id}`);
  };

  // Download media directly to device (server-side watermarking handles non-admin users)
  const handleDownload = async (item) => {
    if (isDownloading || !item) return;
    setIsDownloading(true);

    try {
      // Extract filename from file_id or URL
      let filename = item.file_id || item.filename;
      if (!filename && item.url) {
        const urlPath = item.url.split('?')[0];
        const parts = urlPath.split('/');
        filename = parts[parts.length - 1];
      }
      if (!filename) filename = 'media';

      const userName = user?.name || user?.email || "Unknown User";

      // ALWAYS route through backend API to get watermarked files for non-admin
      // This ensures server-side watermarks are applied.
      //
      // The session token has to be sent explicitly. It lives in localStorage
      // and normally travels as an Authorization header added by the axios
      // interceptor — but this is a bare fetch(), and `credentials: 'include'`
      // only sends cookies, which don't cross from the frontend domain to the
      // backend one. Without the token this request arrives unauthenticated
      // and comes back 401.
      const sessionToken = localStorage.getItem('session_token');
      let downloadUrl = `${API}/uploads/${filename}?download=true`;
      let fetchOptions = { credentials: 'include' };
      if (sessionToken) {
        downloadUrl += `&token=${encodeURIComponent(sessionToken)}`;
        fetchOptions.headers = { Authorization: `Bearer ${sessionToken}` };
      }

      const response = await fetch(downloadUrl, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Check if we got HTML (error page) instead of media
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        throw new Error('File not found on server');
      }

      let blob = await response.blob();

      // Get extension from content-type or filename
      let ext = 'jpg';
      if (contentType.includes('png')) ext = 'png';
      else if (contentType.includes('gif')) ext = 'gif';
      else if (contentType.includes('webp')) ext = 'webp';
      else if (contentType.includes('mp4') || contentType.includes('video')) ext = 'mp4';
      else if (contentType.includes('mp3') || contentType.includes('audio')) ext = 'mp3';
      else if (filename && filename.includes('.')) ext = filename.split('.').pop();

      // Server-side watermarking already applied for non-admin users
      // No client-side watermark needed

      const timestamp = Date.now();
      const downloadFilename = `CHSP_${timestamp}.${ext}`;

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create hidden anchor and trigger download
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = blobUrl;
      anchor.download = downloadFilename;

      document.body.appendChild(anchor);
      anchor.click();

      setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      toast.success('Downloaded!');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed: ' + err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Image className="w-6 h-6 text-purple-400" />
          <h1 className="text-2xl font-bold uppercase tracking-wider">Media</h1>
          <Badge variant="outline">{filteredMedia.length}{hasActiveFilters ? ` / ${media.length}` : ""}</Badge>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMedia} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="bg-slate-900 border-slate-800 mb-4">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search media..."
                className="pl-10 bg-slate-800 border-slate-700"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "border-purple-500 text-purple-400" : ""}
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-purple-600 text-white text-xs px-1.5">!</Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-400 hover:text-white">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-4">
              {/* Row 1: Specific Incident, Perpetrator Profile Media, Linked Perpetrator */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Specific Incident - Searchable */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Incident
                  </Label>
                  <SearchableSelect
                    value={filterIncidentId || "all"}
                    onValueChange={(v) => {
                      setFilterIncidentId(v === "all" ? "" : v);
                      if (v !== "all") {
                        setFilterSourceType("incident");
                        setFilterProfileId("");
                      }
                    }}
                    placeholder="All Incidents"
                    options={[
                      { value: "all", label: "All Incidents" },
                      ...incidents
                        .map((inc) => ({
                          value: inc.incident_id,
                          label: inc.headline || inc.incident_type || inc.incident_id
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    ]}
                  />
                </div>

                {/* Perpetrator Profile Media - Searchable */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block flex items-center gap-1">
                    <User className="w-3 h-3" /> Perp Profile Media
                  </Label>
                  <SearchableSelect
                    value={filterProfileId || "all"}
                    onValueChange={(v) => {
                      setFilterProfileId(v === "all" ? "" : v);
                      if (v !== "all") {
                        setFilterSourceType("perpetrator");
                        setFilterIncidentId("");
                      }
                    }}
                    placeholder="All Perpetrators"
                    options={[
                      { value: "all", label: "All Perpetrators" },
                      ...perpetrators
                        .map((perp) => ({
                          value: perp.profile_id,
                          label: perp.name || "Unknown"
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    ]}
                  />
                </div>

                {/* Linked Perpetrator (perps linked to incidents) - Searchable */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> Linked Perpetrator
                  </Label>
                  <SearchableSelect
                    value={filterLinkedPerpId || "all"}
                    onValueChange={(v) => {
                      setFilterLinkedPerpId(v === "all" ? "" : v);
                      if (v !== "all") {
                        // Clear other filters but DON'T set source type -
                        // we want to show both perp's own media AND incident media
                        setFilterProfileId("");
                        setFilterIncidentId("");
                        setFilterSourceType(""); // Allow both source types
                      }
                    }}
                    placeholder="All Linked Perps"
                    options={[
                      { value: "all", label: "All Linked Perps" },
                      ...linkedPerpetrators
                        .map((perp) => ({
                          value: perp.profile_id,
                          label: perp.name || "Unknown"
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    ]}
                  />
                </div>

                {/* Incident Type - Searchable */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Incident Type</Label>
                  <SearchableSelect
                    value={filterType || "all"}
                    onValueChange={(v) => setFilterType(v === "all" ? "" : v)}
                    placeholder="All Types"
                    options={[
                      { value: "all", label: "All Types" },
                      ...incidentTypes
                        .map((type) => ({
                          value: type.name,
                          label: type.name
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    ]}
                  />
                </div>
              </div>

              {/* Row 2: Source Type, Media Type, Date Range */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* Source Type */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Source Type</Label>
                  <SearchableSelect
                    value={filterSourceType || "all"}
                    onValueChange={(v) => {
                      setFilterSourceType(v === "all" ? "" : v);
                      if (v !== "incident") setFilterIncidentId("");
                      if (v !== "perpetrator") setFilterProfileId("");
                    }}
                    placeholder="All Sources"
                    options={[
                      { value: "all", label: "All Sources" },
                      { value: "incident", label: "Incidents" },
                      { value: "perpetrator", label: "Perpetrator Profiles" }
                    ]}
                  />
                </div>

                {/* Media Type */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Media Type</Label>
                  <SearchableSelect
                    value={filterMediaType || "all"}
                    onValueChange={(v) => setFilterMediaType(v === "all" ? "" : v)}
                    placeholder="All Media"
                    options={[
                      { value: "all", label: "All Media" },
                      { value: "image", label: "Images" },
                      { value: "video", label: "Videos" },
                      { value: "audio", label: "Audio" }
                    ]}
                  />
                </div>

                {/* Start Date */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> From
                  </Label>
                  <Input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="h-9 bg-slate-900 border-slate-700"
                  />
                </div>

                {/* End Date */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> To
                  </Label>
                  <Input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="h-9 bg-slate-900 border-slate-700"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredMedia.length === 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-400">
              {hasActiveFilters ? "No media matches your filters" : "No media found"}
            </p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2 text-purple-400">
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Media Grid - 3 columns */}
      {!loading && filteredMedia.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {filteredMedia.map((item, index) => {
            const isDriveItem = item.source_type === "google_drive";
            return (
            <div
              key={item.file_id || index}
              className="relative aspect-square bg-slate-800 rounded-lg overflow-hidden cursor-pointer border border-slate-700 hover:border-purple-500 transition-colors group"
              onClick={() => setSelectedMedia(item)}
            >
              {/* Thumbnail */}
              {isDriveItem ? (
                <>
                  {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt={item.filename || ""}
                      className="w-full h-full object-cover pointer-events-none"
                      draggable="false"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fb = e.target.parentElement?.querySelector('[data-drive-fallback]');
                        if (fb) fb.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    data-drive-fallback
                    className="w-full h-full items-center justify-center bg-slate-900 text-slate-400"
                    style={{ display: item.thumbnail_url ? "none" : "flex" }}
                  >
                    <svg width="48" height="48" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                    </svg>
                  </div>
                </>
              ) : isImage(item) ? (
                <>
                  <img
                    src={getThumbnailUrl(item)}
                    alt=""
                    className="w-full h-full object-cover pointer-events-none"
                    draggable="false"
                    onError={(e) => {
                      // Fallback to full URL if thumbnail fails
                      if (!e.target.dataset.fallback) {
                        e.target.dataset.fallback = '1';
                        e.target.src = getMediaUrl(item);
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                </>
              ) : isVideo(item) ? (
                <div className="w-full h-full relative bg-slate-900">
                  {/* Use stored thumbnail if available, otherwise try to show video frame */}
                  {item.thumbnail_url ? (
                    <img
                      src={getThumbnailUrl(item)}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover pointer-events-none"
                      draggable="false"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    // Deliberately NOT a <video> element. This used to embed
                    // the real file to scrub out a poster frame, which was
                    // harmless only while these URLs were relative and failed
                    // instantly. Pointed at the API it downloads the WHOLE
                    // file per tile — /api/uploads/<file> has no Range support,
                    // so preload="metadata" gets the entire video — and with a
                    // grid of them that is hundreds of MB per page load, each
                    // one buffered in server memory.
                    <div className="w-full h-full flex items-center justify-center bg-slate-900">
                      <Video className="w-10 h-10 text-slate-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                    <div className="bg-black/50 rounded-full p-2">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  {/* Server-side watermarking handles non-admin users */}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FileText className="w-12 h-12 text-slate-500" />
                </div>
              )}

              {/* Source Label Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                {/* Incident or Perpetrator label */}
                {item.source_type === "perpetrator" ? (
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3 text-orange-400" />
                    <p className="text-[10px] text-orange-300 truncate">{item.profile_name}</p>
                  </div>
                ) : (item.incident_headline || item.incident_address) ? (
                  <p className="text-[10px] text-white truncate">
                    {item.incident_headline || item.incident_address}
                  </p>
                ) : item.filename ? (
                  // Standalone library items (e.g. watermarked exports) have no
                  // incident to name, so fall back to the filename rather than
                  // leaving the tile unlabelled.
                  <p className="text-[10px] text-slate-300 truncate">{item.filename}</p>
                ) : null}
              </div>

              {/* Media Type Badge */}
              <div className="absolute top-2 right-2 flex gap-1">
                {item.source_type === "perpetrator" && (
                  <Badge className="bg-orange-600/80 text-white text-[10px] px-1.5 py-0.5">
                    <User className="w-3 h-3" />
                  </Badge>
                )}
                {isVideo(item) && (
                  <Badge className="bg-blue-600/80 text-white text-[10px] px-1.5 py-0.5">
                    <Video className="w-3 h-3" />
                  </Badge>
                )}
                {isAudio(item) && (
                  <Badge className="bg-green-600/80 text-white text-[10px] px-1.5 py-0.5">
                    <Music className="w-3 h-3" />
                  </Badge>
                )}
                {isDriveItem && (
                  <Badge className="bg-amber-600/80 text-white text-[10px] px-1.5 py-0.5">
                    Drive
                  </Badge>
                )}
                {item.watermarked && (
                  <Badge className="bg-purple-600/80 text-white text-[10px] px-1.5 py-0.5">
                    Watermarked
                  </Badge>
                )}
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
          {/* Close button */}
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-3 right-3 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white z-50"
          >
            <X className="w-5 h-5" />
          </button>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-10">
              {selectedMedia?.source_type === "perpetrator" ? (
                <>
                  <User className="w-5 h-5 text-orange-400" />
                  {selectedMedia?.profile_name || "Perpetrator Media"}
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 text-blue-400" />
                  {/* Standalone library files have no incident to name, so fall
                      back to the filename rather than an anonymous "Media". */}
                  <span className="truncate">
                    {selectedMedia?.incident_headline
                      || selectedMedia?.incident_type
                      || selectedMedia?.filename
                      || "Media"}
                  </span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {/* Media Display - server-side watermarking handles non-admin users.
                Drive items can't be embedded reliably, so we just show the
                thumbnail (or a Drive icon fallback) with an Open-in-Drive
                button below. */}
            {selectedMedia?.source_type === "google_drive" ? (
              <div
                className="relative rounded-lg overflow-hidden bg-slate-950 cursor-pointer flex items-center justify-center"
                onClick={() => selectedMedia?.url && window.open(selectedMedia.url, "_blank", "noopener")}
                title="Open in Google Drive"
              >
                {selectedMedia.thumbnail_url ? (
                  <img
                    src={selectedMedia.thumbnail_url}
                    alt={selectedMedia.filename || ""}
                    className="w-full max-h-[60vh] object-contain"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = "none";
                      const fb = e.target.parentElement?.querySelector('[data-drive-fallback]');
                      if (fb) fb.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  data-drive-fallback
                  className="w-full h-48 items-center justify-center text-slate-400"
                  style={{ display: selectedMedia.thumbnail_url ? "none" : "flex" }}
                >
                  <svg width="64" height="64" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                  </svg>
                </div>
              </div>
            ) : selectedMedia && isImage(selectedMedia) ? (
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={getMediaUrl(selectedMedia)}
                  alt=""
                  className="w-full max-h-[60vh] object-contain"
                />
              </div>
            ) : selectedMedia && isVideo(selectedMedia) ? (
              <div className="relative rounded-lg overflow-hidden">
                <video
                  src={getMediaUrl(selectedMedia)}
                  controls
                  controlsList={user?.role !== "admin" ? "nodownload" : undefined}
                  onContextMenu={(e) => user?.role !== "admin" && e.preventDefault()}
                  playsInline
                  webkit-playsinline="true"
                  preload="auto"
                  className="w-full max-h-[60vh]"
                />
              </div>
            ) : selectedMedia ? (
              // Neither image nor video. This used to render nothing at all,
              // leaving an empty dialog with no clue what the file was or why
              // it wouldn't show. Say what it is and keep Download working.
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-6 text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-slate-500" />
                <p className="text-sm text-slate-200 break-all">
                  {selectedMedia.filename || selectedMedia.file_id}
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {selectedMedia.content_type
                    ? `${selectedMedia.content_type} — can't be previewed here`
                    : "This file has no recorded type, so it can't be previewed here"}
                </p>
                <p className="text-[11px] text-slate-500 mt-2">
                  Use Download below to open it.
                </p>
              </div>
            ) : null}

            {/* Info Box */}
            <div className="mt-4 p-4 bg-slate-800 rounded-lg space-y-2">
              {selectedMedia?.source_type === "perpetrator" ? (
                <>
                  {/* Perpetrator Info */}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Source:</span>
                    <Badge className="bg-orange-600">Perpetrator Profile</Badge>
                  </div>

                  {selectedMedia?.profile_name && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Name:</span>
                      <span className="text-white font-medium">{selectedMedia.profile_name}</span>
                    </div>
                  )}

                  {selectedMedia?.media_subtype && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Type:</span>
                      <Badge variant="outline" className="text-orange-400 border-orange-600 capitalize">
                        {selectedMedia.media_subtype.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  )}

                  {selectedMedia?.uploaded_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Uploaded:</span>
                      <span className="text-white">
                        {new Date(selectedMedia.uploaded_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Incident Info */}
                  {selectedMedia?.incident_type && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Incident Type:</span>
                      <Badge className="bg-blue-600">{selectedMedia.incident_type}</Badge>
                    </div>
                  )}

                  {selectedMedia?.incident_headline && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Headline:</span>
                      <span className="text-white font-medium">{selectedMedia.incident_headline}</span>
                    </div>
                  )}

                  {selectedMedia?.incident_address && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Location:</span>
                      <span className="text-white">{selectedMedia.incident_address}</span>
                    </div>
                  )}

                  {selectedMedia?.incident_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Date:</span>
                      <span className="text-white">
                        {new Date(selectedMedia.incident_date).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}

                  {selectedMedia?.from_live_call && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Source:</span>
                      <Badge variant="outline" className="text-green-400 border-green-600">From Live Call</Badge>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex flex-wrap gap-2">
              {/* Go to Incident or Profile */}
              {selectedMedia?.source_type === "perpetrator" && selectedMedia?.profile_id ? (
                <Button onClick={() => goToProfile(selectedMedia)} className="flex-1 bg-orange-600 hover:bg-orange-700">
                  <User className="w-4 h-4 mr-2" />
                  Go to Profile
                </Button>
              ) : selectedMedia?.incident_id && (
                <Button onClick={() => goToSource(selectedMedia)} className="flex-1">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Go to Incident
                </Button>
              )}

              {/* Drive items: open in Drive (no Download — Drive has its own) */}
              {selectedMedia?.source_type === "google_drive" ? (
                <Button
                  variant="outline"
                  onClick={() => selectedMedia?.url && window.open(selectedMedia.url, "_blank", "noopener")}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Drive
                </Button>
              ) : !(selectedMedia && isVideo(selectedMedia) && user?.role !== "admin") && (
                <Button
                  variant="outline"
                  onClick={() => handleDownload(selectedMedia)}
                  disabled={isDownloading}
                  className="flex-1"
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {isDownloading ? 'Downloading...' : 'Download'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
